import { Loader2, Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { IconButton } from 'src/tef/components/buttons/IconButton';
import { TextInput } from 'src/tef/components/forms/TextInput';
import { ContainerBox } from 'src/tef/components/primitives/ContainerBox';
import { cn } from 'src/tef/components/utils/cn';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onSearch?: (value: string) => void;
  debounceMs?: number;
  loading?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  variant?: {
    size?: 'sm' | 'md' | 'lg';
  };
  className?: string;
  id?: string;
  name?: string;
}

const sizeStyles = {
  sm: 'py-1.5 pl-10 pr-10 text-sm',
  md: 'py-2 pl-10 pr-10 text-base',
  lg: 'py-3 pl-12 pr-12 text-lg',
};

const iconSizes = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

const iconPositions = {
  sm: 'left-3',
  md: 'left-3.5',
  lg: 'left-4',
};

const clearPositions = {
  sm: 'right-2',
  md: 'right-2.5',
  lg: 'right-3',
};

export default function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  onSearch,
  debounceMs = 0,
  loading = false,
  disabled = false,
  autoFocus = false,
  variant = {},
  className,
  id,
  name,
}: SearchInputProps) {
  const size = variant.size || 'md';
  const [internalValue, setInternalValue] = useState(value);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const uniqueId = useId();
  const inputId = id || uniqueId;

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);

      if (debounceMs > 0) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
          onChange(newValue);
          onSearch?.(newValue);
        }, debounceMs);
      } else {
        onChange(newValue);
      }
    },
    [onChange, onSearch, debounceMs]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onSearch) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        onChange(internalValue);
        onSearch(internalValue);
      } else if (e.key === 'Escape') {
        inputRef.current?.blur();
      }
    },
    [onSearch, onChange, internalValue]
  );

  const handleClear = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setInternalValue('');
    onChange('');
    onSearch?.('');
    inputRef.current?.focus();
  }, [onChange, onSearch]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <ContainerBox
      as="section"
      className={cn(
        'relative flex items-stretch',
        'rounded-xl',
        'bg-white dark:bg-slate-800',
        'border border-slate-200 dark:border-slate-700',
        'hover:border-slate-300 dark:hover:border-slate-600',
        'focus-within:border-primary',
        'transition-colors duration-200',
        className
      )}
    >
      <ContainerBox
        className={cn('absolute inset-y-0 flex items-center pointer-events-none z-10', iconPositions[size])}
        aria-hidden="true"
      >
        {loading ? (
          <Loader2 className={cn(iconSizes[size], 'animate-spin text-slate-400 dark:text-slate-300')} />
        ) : (
          <Search className={cn(iconSizes[size], 'text-slate-400 dark:text-slate-300')} />
        )}
      </ContainerBox>
      <ContainerBox className="flex-1 min-w-0 [&>div]:w-full [&>div]:h-full [&_input]:w-full [&_input]:h-full">
        <TextInput
          ref={inputRef}
          type="search"
          id={inputId}
          name={name}
          value={internalValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown as React.KeyboardEventHandler<HTMLInputElement>}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          aria-label={placeholder}
          variant={{ size }}
          borderless
          className={cn(
            'w-full h-full rounded-xl',
            'bg-transparent border-0 shadow-none ring-0',
            'focus:ring-0 focus:outline-none focus:border-0',
            '[&::-webkit-search-cancel-button]:hidden',
            sizeStyles[size]
          )}
        />
      </ContainerBox>
      {internalValue && !loading && !disabled && (
        <IconButton
          icon={<X className={cn(iconSizes[size])} />}
          variant={{ style: 'ghost', size: 'xs' }}
          onClick={handleClear}
          aria-label="Clear search"
          className={cn(
            'absolute top-1/2 -translate-y-1/2 z-10',
            clearPositions[size],
            'text-slate-400 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-200'
          )}
        />
      )}
    </ContainerBox>
  );
}

export { SearchInput };
